// utils/parseScanReport.js

/**
 * Parses the HTML report and extracts structured data
 * @param {string} htmlString - The raw HTML report string
 * @returns {object} Parsed data containing agents, tools, mcpServers, vulnerabilities, and graphData
 */
export const parseScanReport = (htmlString) => {
  try {
    // Create a temporary DOM element to parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    // Initialize result object
    const result = {
      agents: [],
      tools: [],
      mcpServers: [],
      vulnerabilities: [],
      graphData: null,
      scanDetails: {},
      findings: {}
    };

    // ===== 1. EXTRACT SCAN DETAILS =====
    const infoGridItems = doc.querySelectorAll('.info-grid .info-value');
    if (infoGridItems.length >= 3) {
      result.scanDetails = {
        projectName: infoGridItems[0]?.textContent.trim() || '',
        timestamp: infoGridItems[1]?.textContent.trim() || '',
        framework: infoGridItems[2]?.textContent.trim() || ''
      };
    }

    // ===== 2. EXTRACT FINDINGS SUMMARY =====
    const findingCards = doc.querySelectorAll('.finding-card');
    findingCards.forEach(card => {
      const label = card.querySelector('.finding-label')?.textContent.trim().toLowerCase();
      const value = parseInt(card.querySelector('.finding-value')?.textContent.trim() || '0', 10);
      
      if (label) {
        result.findings[label] = value;
      }
    });

    // ===== 3. EXTRACT GRAPH DATA FROM INLINE SCRIPT =====
    const scripts = doc.querySelectorAll('script');
    scripts.forEach(script => {
      const scriptContent = script.textContent || '';
      
      // Look for __INLINE_DATA pattern
      if (scriptContent.includes('__INLINE_DATA')) {
        try {
          // Extract the data between __INLINE_DATA = { ... }
          const dataMatch = scriptContent.match(/__INLINE_DATA\s*=\s*({[\s\S]*?});/);
          if (dataMatch && dataMatch[1]) {
            const inlineData = JSON.parse(dataMatch[1]);
            
            if (inlineData.nodes && inlineData.edges) {
              result.graphData = {
                nodes: inlineData.nodes.map(node => ({
                  id: node.name,
                  name: node.name,
                  label: node.label || node.name,
                  type: node.type || 'unknown',
                  group: node.group || 0,
                  image: node.image || null
                })),
                links: inlineData.edges.map(edge => ({
                  source: edge.source,
                  target: edge.target
                }))
              };
            }
          }
        } catch (e) {
          console.warn('Failed to parse inline graph data:', e);
        }
      }
    });

    // ===== 4. EXTRACT TOOLS FROM TABLE =====
    const toolRows = doc.querySelectorAll('.data-table tbody tr');
    toolRows.forEach((row, index) => {
      const cells = row.querySelectorAll('td');
      if (cells.length >= 4) {
        const tool = {
          id: `tool_${index}`,
          name: cells[0]?.textContent.trim() || '',
          category: cells[1]?.textContent.trim() || '',
          type: cells[1]?.textContent.trim() || '', // Using category as type
          description: cells[2]?.textContent.trim().replace(/&amp;#x27;/g, "'") || '',
          vulnerabilityCount: parseInt(cells[3]?.textContent.trim() || '0', 10),
          risk: determineRiskLevel(parseInt(cells[3]?.textContent.trim() || '0', 10))
        };
        result.tools.push(tool);
      }
    });

    // ===== 5. EXTRACT VULNERABILITIES =====
    const vulnSections = doc.querySelectorAll('.vulnerability-section');
    let vulnIdCounter = 1;

    vulnSections.forEach(section => {
      const toolName = section.querySelector('.vulnerability-header')?.textContent.trim() || 'Unknown';
      const vulnDetails = section.querySelectorAll('.vulnerability-details');

      vulnDetails.forEach(detail => {
        const detailRows = detail.querySelectorAll('.detail-row');
        const vuln = {
          id: `vuln_${vulnIdCounter++}`,
          toolName: toolName,
          name: '',
          description: '',
          severity: 'medium', // default
          framework: '',
          affected: [toolName],
          mitigated: false,
          remediationSteps: []
        };

        detailRows.forEach(row => {
          const label = row.querySelector('.detail-label')?.textContent.trim().toLowerCase() || '';
          const value = row.querySelector('.detail-value');

          if (!value) return;

          if (label.includes('vulnerability')) {
            vuln.name = value.querySelector('strong')?.textContent.trim() || value.textContent.trim();
            // Determine severity from vulnerability name
            vuln.severity = determineSeverityFromName(vuln.name);
          } else if (label.includes('description')) {
            vuln.description = value.textContent.trim();
          } else if (label.includes('security framework')) {
            const frameworks = [];
            value.querySelectorAll('strong').forEach(fw => {
              frameworks.push(fw.textContent.trim());
            });
            vuln.framework = frameworks.join(', ') || 'OWASP LLM Top 10';
          } else if (label.includes('remediation')) {
            const steps = value.textContent.trim().split('•').filter(s => s.trim());
            vuln.remediationSteps = steps.map(s => s.trim());
          }
        });

        if (vuln.name) {
          result.vulnerabilities.push(vuln);
        }
      });
    });

    // ===== 6. EXTRACT AGENTS (from graph nodes or other sources) =====
    if (result.graphData && result.graphData.nodes) {
      result.graphData.nodes.forEach(node => {
        // Check if node represents an agent (based on type or naming convention)
        if (node.type === 'agent' || node.name.toLowerCase().includes('agent')) {
          result.agents.push({
            id: node.id,
            name: node.name,
            type: node.type || 'generic',
            systemPrompt: 'Unknown', // This would need to be in the data
            tools: result.tools.length // Simplified - would need actual mapping
          });
        }
      });
    }

    // If no agents found from graph, check findings
    if (result.agents.length === 0 && result.findings.agents > 0) {
      // Create placeholder agents based on count
      for (let i = 0; i < result.findings.agents; i++) {
        result.agents.push({
          id: `agent_${i + 1}`,
          name: `Agent ${i + 1}`,
          type: 'generic',
          systemPrompt: 'Unknown',
          tools: 0
        });
      }
    }

    // ===== 7. EXTRACT MCP SERVERS (from graph or other sources) =====
    if (result.graphData && result.graphData.nodes) {
      result.graphData.nodes.forEach(node => {
        if (node.type === 'mcp_server' || node.name.toLowerCase().includes('mcp')) {
          result.mcpServers.push({
            id: node.id,
            name: node.name,
            status: 'active',
            connections: 0 // Would need actual connection data
          });
        }
      });
    }

    console.log('✅ Successfully parsed scan report:', result);
    return result;

  } catch (error) {
    console.error('❌ Failed to parse scan report:', error);
    return {
      agents: [],
      tools: [],
      mcpServers: [],
      vulnerabilities: [],
      graphData: null,
      scanDetails: {},
      findings: {},
      error: error.message
    };
  }
};

/**
 * Helper function to determine risk level based on vulnerability count
 */
const determineRiskLevel = (vulnCount) => {
  if (vulnCount === 0) return 'low';
  if (vulnCount === 1) return 'medium';
  if (vulnCount >= 2) return 'high';
  return 'low';
};

/**
 * Helper function to determine severity from vulnerability name
 */
const determineSeverityFromName = (name) => {
  const lowerName = name.toLowerCase();
  
  if (lowerName.includes('injection') || lowerName.includes('disclosure')) {
    return 'critical';
  }
  if (lowerName.includes('authentication') || lowerName.includes('authorization')) {
    return 'high';
  }
  if (lowerName.includes('validation') || lowerName.includes('sanitization')) {
    return 'medium';
  }
  
  return 'medium'; // default
};

/**
 * Alternative parser for cases where the HTML structure is different
 * This uses regex patterns to extract data directly from the HTML string
 */
export const parseScanReportFallback = (htmlString) => {
  const result = {
    agents: [],
    tools: [],
    mcpServers: [],
    vulnerabilities: [],
    graphData: null,
    scanDetails: {},
    findings: {}
  };

  try {
    // Extract findings using regex
    const vulnerabilitiesMatch = htmlString.match(/<div class="finding-label">Vulnerabilities<\/div>\s*<div class="finding-value">(\d+)<\/div>/);
    const agentsMatch = htmlString.match(/<div class="finding-label">Agents<\/div>\s*<div class="finding-value">(\d+)<\/div>/);
    const toolsMatch = htmlString.match(/<div class="finding-label">Tools<\/div>\s*<div class="finding-value">(\d+)<\/div>/);

    if (vulnerabilitiesMatch) result.findings.vulnerabilities = parseInt(vulnerabilitiesMatch[1], 10);
    if (agentsMatch) result.findings.agents = parseInt(agentsMatch[1], 10);
    if (toolsMatch) result.findings.tools = parseInt(toolsMatch[1], 10);

    // Extract inline data using regex
    const inlineDataMatch = htmlString.match(/__INLINE_DATA\s*=\s*({[\s\S]*?});/);
    if (inlineDataMatch && inlineDataMatch[1]) {
      try {
        const inlineData = JSON.parse(inlineDataMatch[1]);
        if (inlineData.nodes && inlineData.edges) {
          result.graphData = {
            nodes: inlineData.nodes.map(node => ({
              id: node.name || node.id || node.label,
              name: node.name || node.id || node.label,
              label: node.label || node.name || node.id,
              type: node.type || 'unknown',
              group: node.group || 0,
              image: node.image || null
            })),
            links: inlineData.edges.map(edge => ({
              source: edge.source,
              target: edge.target
            }))
          };
        }
      } catch (e) {
        console.warn('Failed to parse inline data with fallback:', e);
      }
    }

    return result;
  } catch (error) {
    console.error('Fallback parser failed:', error);
    return result;
  }
};

export default parseScanReport;
